import * as React from "react";
import { useDispatch } from "react-redux";

import {
  Close,
  IconButton,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Divider,
  Checkbox,
  FormControlLabel,
  TextField,
  Alert,
  Radio,
} from "mpulse-design-system";
import { styled, FormGroup } from "@mui/material";

import { API_V1_AUDIO_FILE_REVIEW_REQUEST } from "../../redux/audioFile/actionTypes";
import AudioRejectionReason from "../../utils/enums/AudioRejectionReason";

import PopupLayout from "../PopupLayout/PopupLayout";

// TODO: Add better field validations
const AudioReviewActionPopup = ({
  hidden,
  action,
  setPopupProps,
  index,
  id,
  content,
  type,
  language,
  metadataLabel,
  accountId,
}) => {
  const defaultState = {
    pronunciation: false,
    contentIncorrect: false,
    missingAudioContent: false,
    wrongMetadataId: false,
    showError: false,
  };
  const [state, setState] = React.useState(defaultState);
  const dispatch = useDispatch();

  const isReject = action === "REJECT",
    title = isReject ? "Reject" : "Approve",
    width = isReject ? "556px" : "371px";

  const handleClose = () => {
    setPopupProps({ hidden: true });
    setState(defaultState);
  };

  const handleChange = (e) => {
    setState({
      ...defaultState,
      [e.target.id]: e.target.checked,
    });
  };

  const saveInput = (e) => {
    const key =
      e.target.id === "missingAudioContent-notes"
        ? "missingAudioContentReason"
        : e.target.id === "pronunciation-notes"
        ? "pronunciationReason"
        : e.target.id === "contentIncorrect-notes"
        ? "contentIncorrectReason"
        : e.target.id === "wrongMetadataID-notes"
        ? "wrongMetadataIDReason"
        : "reason";
    setState({ ...state, [key]: e.target.value });
  };

  const validateRejectionReason = () => {
    let reasonProvidedAndValid = false;
    let reason = null;
    let comments = null;

    if (state.contentIncorrect && state.contentIncorrectReason) {
      reasonProvidedAndValid = state.contentIncorrectReason.length >= 1;
      reason = AudioRejectionReason.CONTENT_RECORDED_INCORRECTLY;
      comments = state.contentIncorrectReason;
    } else if (state.pronunciation && state.pronunciationReason) {
      reasonProvidedAndValid = state.pronunciationReason.length >= 1;
      reason = AudioRejectionReason.PRONUNCIATION;
      comments = state.pronunciationReason;
    } else if (state.missingAudioContent && state.missingAudioContentReason) {
      reasonProvidedAndValid = state.missingAudioContentReason.length >= 1;
      reason = AudioRejectionReason.MISSING_OR_INCORRECT_AUDIO_CONTENT;
      comments = state.missingAudioContentReason;
    } else if (state.wrongMetadataId) {
      reasonProvidedAndValid = true;
      reason = AudioRejectionReason.AUDIO_NOT_TIED_TO_CORRECT_METADATA;
      comments = state.wrongMetadataIDReason || "";
    }

    return { reasonProvidedAndValid, reason, comments };
  };

  const handleAction = () => {
    let doAction = true;
    let data = {};
    if (action === "REJECT") {
      const { reasonProvidedAndValid, reason, comments } =
        validateRejectionReason();
      doAction = reasonProvidedAndValid;
      data.reason = reason;
      data.comments = comments;
    }

    if (doAction) {
      handleClose();
      dispatch({
        type: API_V1_AUDIO_FILE_REVIEW_REQUEST,
        action,
        accountId: accountId,
        audioFileId: id,
        index,
        data,
      });
    } else {
      setState({
        ...state,
        showError: true,
      });
    }
  };

  const {
    pronunciation,
    contentIncorrect,
    missingAudioContent,
    wrongMetadataId,
  } = state;

  return !hidden ? (
    <PopupLayout>
      <Paper
        elevation={3}
        sx={{
          height: "fit-content",
          width,
          padding: (theme) => theme.spacing(3),
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            marginBottom: (theme) => theme.spacing(3),
          }}
        >
          <Typography color="textPrimary" variant="h3" sx={{ flexGrow: 1 }}>
            {title} Audio File
          </Typography>
          <StyledIconButton color="tertiary" onClick={handleClose}>
            <Close color="tertiary" />
          </StyledIconButton>
        </Box>
        {isReject ? (
          <FormGroup>
            <Typography color="textPrimary" variant="body1">
              {" "}
              Please select a reason:{" "}
            </Typography>
            <Grid container direction="column">
              {state.showError ? (
                <Grid item>
                  <Alert severity="error">
                    Please select an option and fill out any required inputs.
                  </Alert>
                </Grid>
              ) : null}
              <Grid item>
                <FormControlLabel
                  control={
                    <Radio
                      id="pronunciation"
                      checked={pronunciation}
                      size="medium"
                      onChange={handleChange}
                    />
                  }
                  label="Pronunciation"
                />
              </Grid>
              <Grid
                item
                sx={{ padding: (theme) => `0 ${theme.spacing(3)}` }}
                hidden={!pronunciation}
              >
                <TextField
                  id="pronunciation-notes"
                  color="secondary"
                  label="PRONUNCIATION NOTES"
                  placeholder="Input Text"
                  type="text"
                  onBlur={saveInput}
                  required
                />
              </Grid>
              <Grid item>
                <FormControlLabel
                  control={
                    <Radio
                      checked={contentIncorrect}
                      id="contentIncorrect"
                      size="medium"
                      onChange={handleChange}
                    />
                  }
                  label="Content recorded incorrectly"
                />
              </Grid>
              <Grid
                item
                sx={{ padding: (theme) => `0 ${theme.spacing(3)}` }}
                hidden={!contentIncorrect}
              >
                <TextField
                  id="contentIncorrect-notes"
                  color="secondary"
                  label="CORRECTION NOTES"
                  placeholder="Input Text"
                  type="text"
                  onBlur={saveInput}
                  required
                />
              </Grid>
              <Grid item>
                <FormControlLabel
                  control={
                    <Radio
                      checked={missingAudioContent}
                      id="missingAudioContent"
                      size="medium"
                      onChange={handleChange}
                    />
                  }
                  label="Missing/Incorrect audio content"
                />
              </Grid>
              <Grid
                item
                sx={{ padding: (theme) => `0 ${theme.spacing(3)}` }}
                hidden={!missingAudioContent}
              >
                <TextField
                  id="missingAudioContent-notes"
                  color="secondary"
                  label="CORRECTION NOTES"
                  placeholder="Input Text"
                  type="text"
                  onBlur={saveInput}
                  required
                />
              </Grid>
              <Grid item>
                <FormControlLabel
                  control={
                    <Radio
                      checked={wrongMetadataId}
                      id="wrongMetadataId"
                      size="medium"
                      onChange={handleChange}
                    />
                  }
                  label={`Audio not tied to correct ${metadataLabel.toLowerCase()} id`}
                />
              </Grid>
              <Grid
                item
                sx={{ padding: (theme) => `0 ${theme.spacing(3)}` }}
                hidden={!wrongMetadataId}
              >
                <TextField
                  id="wrongMetadataID-notes"
                  color="secondary"
                  label="CORRECTION NOTES"
                  placeholder="Input Text"
                  type="text"
                  onBlur={saveInput}
                />
              </Grid>
              <Grid item>
                <Divider sx={{ margin: (theme) => `${theme.spacing(3)} 0` }} />
              </Grid>
            </Grid>
          </FormGroup>
        ) : null}
        <Grid container rowSpacing={2}>
          <Grid item xxs={12}>
            <Typography color="textPrimary" variant="h4">
              Audio Details
            </Typography>
          </Grid>
          <Grid item container xxs={12} rowSpacing={3}>
            <Grid item container xxs={12} rowSpacing={1}>
              <Grid item xxs={12}>
                <Typography color="textPrimary" variant="body1">
                  <b>Content:</b>
                </Typography>
              </Grid>
              <Grid item xs="auto">
                <Typography color="textSecondary" variant="body1">
                  {content}
                </Typography>
              </Grid>
            </Grid>
            <Grid item container xxs={12} rowSpacing={1}>
              <Grid item xxs={12}>
                <Typography color="textPrimary" variant="body1">
                  <b>File Type:</b>
                </Typography>
              </Grid>
              <Grid item xs="auto">
                <Typography color="textSecondary" variant="body1">
                  {type}
                </Typography>
              </Grid>
            </Grid>
            <Grid item container xxs={12} rowSpacing={1}>
              <Grid item xxs={12}>
                <Typography color="textPrimary" variant="body1">
                  <b>Language:</b>
                </Typography>
              </Grid>
              <Grid item xs="auto">
                <Typography color="textSecondary" variant="body1">
                  {language}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", marginTop: (theme) => theme.spacing(4) }}>
          <Box sx={{ flexGrow: 1 }}>
            <Button
              id="audio-actions"
              variant="outlined"
              size="medium"
              onClick={handleClose}
            >
              CANCEL
            </Button>
          </Box>
          <Button
            id="audio-actions"
            variant="contained"
            size="medium"
            onClick={handleAction}
          >
            {action}
          </Button>
        </Box>
      </Paper>
    </PopupLayout>
  ) : null;
};

const StyledIconButton = styled(IconButton)(() => ({
  top: "2px",
  right: "4px",
  position: "absolute",
}));

export default AudioReviewActionPopup;
